import { create } from "zustand";
import type { StateStorage } from "zustand/middleware";
import { createJSONStorage, persist } from "zustand/middleware";

import type { Repo } from "./types";
import { MessageOrigin } from "./types";

const getCookies: () => Record<string, string> = () =>
  document.cookie
    ? document.cookie
        .split(";")
        .map((v) => v.split("="))
        .reduce<{ [key: string]: string }>((acc, v) => {
          acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());

          return acc;
        }, {})
    : {};

const createCookie = (name: string, value: string, hours: number) => {
  let expires;

  if (hours) {
    const date = new Date();

    date.setTime(date.getTime() + hours * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  } else {
    expires = "";
  }
  document.cookie =
    encodeURIComponent(name) + "=" + encodeURIComponent(value) + expires + "; path=/";
};

const cookieStorage: StateStorage = {
  getItem: (name) => {
    const cookies = getCookies();

    return cookies[name];
  },

  setItem: (name, value) => {
    const cookies = getCookies();

    cookies[name] = value;

    Object.keys(cookies).forEach((cookie) => {
      createCookie(cookie, cookies[cookie], 2);
    });
  },

  removeItem: (name) => {
    const cookies = getCookies();

    delete cookies[name];

    Object.keys(cookies).forEach((cookie) => {
      createCookie(cookie, cookies[cookie], 2);
    });
  },
};

export const messageRepo = create(
  persist<Repo>(
    (set, get) => ({
      receivedMessages: {},
      sentMessages: {},
      currentIndex: 0,
      storeMessage: (origin, message) => {
        const messages =
          origin == MessageOrigin.Received ? get().receivedMessages : get().sentMessages;

        messages[message.id] = message;
        const newId = message.id + 1;

        if (get().currentIndex <= message.id) set({ currentIndex: newId });
      },
      getMessage: (id: number) => {
        const receivedMessage = get().receivedMessages[id];
        const sentMessage = get().sentMessages[id];

        if (receivedMessage) return { ...receivedMessage };
        else if (sentMessage) return { ...sentMessage };
      },
      resolveMessage: (ack) => {
        const message = get().getMessage(ack.id);

        if (message) {
          message.state = ack.state;
          get().storeMessage(MessageOrigin.Sent, message);
        }
      },
      clearStore: () => {
        set({ receivedMessages: {}, sentMessages: {}, currentIndex: 0 });
      },
    }),
    {
      name: "portal-messenger-repo",
      storage: createJSONStorage(() => cookieStorage),
    },
  ),
);
